/**
 * @fileoverview Custom 3D Volume Rendering Presets Engine (v6.2.0 - Quantum Architecture)
 * @module MikhmelViewer/Presets3D
 * 
 * Enterprise Features:
 *   ✓ Strict TypeScript & ReadonlyDeep Guarantees
 *   ✓ Result Pattern (Either Monad) for Error Handling
 *   ✓ Encapsulated PresetRegistry (Singleton/Service)
 *   ✓ Safe VTK Adapter with Interface Segregation
 *   ✓ Reflect-based Deep Freeze with Collection Protection & Cycle Detection
 *   ✓ Runtime Validation on Init
 *   ✓ Dynamic Custom Presets Support
 *   ✓ Modality-based Filtering (CT/MRI/Any)
 */

import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════════
// 1. TYPES & INTERFACES (Strict Duck Typing for VTK)
// ══════════════════════════════════════════════════════════════════

export type ColorPoint = readonly [hu: number, r: number, g: number, b: number];
export type OpacityPoint = readonly [hu: number, alpha: number];
export type GradientPoint = readonly [magnitude: number, alpha: number];

export interface Preset3D {
  readonly name: string;
  readonly category: string;
  readonly modality?: string;
  readonly color: readonly ColorPoint[];
  readonly opacity: readonly OpacityPoint[];
  readonly gradient?: readonly GradientPoint[] | null;
  readonly opacityUnitDistance?: number;
  readonly shade?: boolean;
  readonly ambient?: number;
  readonly diffuse?: number;
  readonly specular?: number;
  readonly specularPower?: number;
}

// VTK Interfaces
interface IVtkTransferFunction {
  removeAllPoints(): void;
  addRGBPoint(x: number, r: number, g: number, b: number): void;
  addPoint(x: number, y: number): void;
}

interface IVtkVolumeProperty {
  getRGBTransferFunction?(index?: number): IVtkTransferFunction | IVtkTransferFunction[] | undefined;
  getScalarOpacity?(index?: number): IVtkTransferFunction | undefined;
  getGradientOpacity?(index?: number): IVtkTransferFunction | undefined;
  setUseGradientOpacity?(index: number, use: boolean): void;
  setShade?(shade: boolean): void;
  setAmbient?(val: number): void;
  setDiffuse?(val: number): void;
  setSpecular?(val: number): void;
  setSpecularPower?(val: number): void;
  setScalarOpacityUnitDistance?(index: number, distance: number): void;
}

interface IVtkVolumeActor {
  getProperty?(): IVtkVolumeProperty | undefined;
  property?: IVtkVolumeProperty;
}

interface IViewport {
  getDefaultActor?(): { actor: IVtkVolumeActor } | undefined;
  getActors?(): Array<{ actor: IVtkVolumeActor } | IVtkVolumeActor>;
  setProperties?(props: Record<string, unknown>): void;
  render(): void;
}

// Result Pattern (Monad)
export type Result<T, E = Error> = 
  | { readonly success: true; readonly value: T }
  | { readonly success: false; readonly error: E };

const Ok = <T>(value: T): Result<T, never> => ({ success: true, value });
const Err = <E>(error: E): Result<never, E> => ({ success: false, error });

// ══════════════════════════════════════════════════════════════════
// 2. ADVANCED UTILITIES
// ══════════════════════════════════════════════════════════════════

/**
 * Рекурсивный тип для глубокой заморозки.
 * Обеспечивает ReadonlyDeep гарантии на уровне типов для всех вложенных структур.
 */
type Primitive = string | number | boolean | bigint | symbol | undefined | null;

type ReadonlyDeep<T> = T extends Primitive
  ? T
  : T extends Function
    ? T
    : T extends Map<infer K, infer V>
      ? ReadonlyMap<ReadonlyDeep<K>, ReadonlyDeep<V>>
      : T extends Set<infer U>
        ? ReadonlySet<ReadonlyDeep<U>>
        : T extends (infer R)[]
          ? ReadonlyArray<ReadonlyDeep<R>>
          : T extends object
            ? { readonly [P in keyof T]: ReadonlyDeep<T[P]> }
            : T;

/**
 * Глубокая заморозка с использованием Reflect API.
 * Защищает от мутаций, обрабатывает Symbol-ы, циклические ссылки и блокирует Map/Set.
 */
const deepFreeze = <T extends object>(obj: T, visited = new WeakSet<object>()): ReadonlyDeep<T> => {
  if (obj === null || typeof obj !== 'object' || Object.isFrozen(obj)) {
    return obj as ReadonlyDeep<T>;
  }

  // Защита от циклических ссылок
  if (visited.has(obj)) {
    return obj as ReadonlyDeep<T>;
  }

  visited.add(obj);

  const keys = Reflect.ownKeys(obj) as Array<keyof T>;
  for (const key of keys) {
    const value = obj[key];
    if (value !== null && typeof value === 'object') {
      deepFreeze(value as object, visited);
    }
  }

  return Object.freeze(obj) as ReadonlyDeep<T>;
};

/**
 * Runtime валидация пресета
 */
function validatePreset(preset: Preset3D, key: string): void {
  if (!preset.name || typeof preset.name !== 'string') {
    throw new Error(`Preset "${key}": missing or invalid 'name'`);
  }
  
  if (!preset.category || typeof preset.category !== 'string') {
    throw new Error(`Preset "${key}": missing or invalid 'category'`);
  }
  
  if (!Array.isArray(preset.color) || preset.color.length === 0) {
    throw new Error(`Preset "${key}": 'color' must be a non-empty array`);
  }
  
  if (!Array.isArray(preset.opacity) || preset.opacity.length === 0) {
    throw new Error(`Preset "${key}": 'opacity' must be a non-empty array`);
  }
  
  // Проверка, что точки отсортированы по возрастанию
  for (let i = 1; i < preset.color.length; i++) {
    if (preset.color[i][0] <= preset.color[i - 1][0]) {
      throw new Error(`Preset "${key}": 'color' points must be sorted by HU value`);
    }
  }
  
  for (let i = 1; i < preset.opacity.length; i++) {
    if (preset.opacity[i][0] <= preset.opacity[i - 1][0]) {
      throw new Error(`Preset "${key}": 'opacity' points must be sorted by HU value`);
    }
  }
  
  if (preset.gradient && Array.isArray(preset.gradient)) {
    for (let i = 1; i < preset.gradient.length; i++) {
      if (preset.gradient[i][0] <= preset.gradient[i - 1][0]) {
        throw new Error(`Preset "${key}": 'gradient' points must be sorted by magnitude value`);
      }
    }
  }
  
  // Проверка диапазонов значений
  for (const [hu, r, g, b] of preset.color) {
    if (typeof hu !== 'number' || typeof r !== 'number' || typeof g !== 'number' || typeof b !== 'number') {
      throw new Error(`Preset "${key}": all color point values must be numbers`);
    }
    if (r < 0 || r > 1 || g < 0 || g > 1 || b < 0 || b > 1) {
      throw new Error(`Preset "${key}": RGB values must be in range [0, 1]`);
    }
  }
  
  for (const [hu, alpha] of preset.opacity) {
    if (typeof hu !== 'number' || typeof alpha !== 'number') {
      throw new Error(`Preset "${key}": all opacity point values must be numbers`);
    }
    if (alpha < 0 || alpha > 1) {
      throw new Error(`Preset "${key}": opacity values must be in range [0, 1]`);
    }
  }
}

// ══════════════════════════════════════════════════════════════════
// 3. CLINICAL PRESETS DEFINITION (Data Layer)
// ══════════════════════════════════════════════════════════════════

const _PRESETS_CONFIG: Record<string, Preset3D> = {

  // ────────────────────────── КОСТИ ──────────────────────────────

  'ct-bone-clean': {
    name: 'Кости (чистые)',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ 150,  0.50, 0.38, 0.28],
      [ 300,  0.82, 0.72, 0.55],
      [ 500,  0.92, 0.87, 0.72],
      [ 800,  0.97, 0.94, 0.84],
      [1200,  0.99, 0.97, 0.91],
      [3000,  1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ 100,  0.00],
      [ 150,  0.00],
      [ 180,  0.15],
      [ 220,  0.35],
      [ 300,  0.55],
      [ 450,  0.75],
      [ 700,  0.92],
      [1200,  0.98],
      [3000,  1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 15, 0.00],
      [ 40, 0.40],
      [100, 0.80],
      [300, 1.00],
    ],
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.30,
    specularPower: 16,
  },

  'ct-bone-context': {
    name: 'Кости + контекст',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  -50, 0.55, 0.40, 0.35],
      [   50, 0.72, 0.52, 0.45],
      [  200, 0.82, 0.72, 0.58],
      [  450, 0.92, 0.87, 0.72],
      [  800, 0.97, 0.94, 0.85],
      [ 2000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [  -50, 0.005],
      [   40, 0.015],
      [  100, 0.015],
      [  200, 0.12],
      [  350, 0.50],
      [  550, 0.80],
      [  900, 0.94],
      [3000,  1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.18,
    diffuse: 0.82,
    specular: 0.22,
    specularPower: 12,
  },

  'ct-bone-hard': {
    name: 'Кости (жёсткий)',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ 250,  0.90, 0.85, 0.70],
      [ 500,  0.96, 0.93, 0.82],
      [1000,  0.99, 0.97, 0.90],
      [3000,  1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ 249,  0.00],
      [ 255,  0.40],
      [ 400,  0.75],
      [ 650,  0.92],
      [1500,  1.00],
      [3000,  1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 60, 0.50],
      [200, 1.00],
    ],
    shade: true,
    ambient: 0.12,
    diffuse: 0.88,
    specular: 0.35,
    specularPower: 20,
  },

  'ct-bone-warm': {
    name: 'Кости (тёплые)',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ 200,  0.60, 0.35, 0.15],
      [ 400,  0.82, 0.55, 0.25],
      [ 650,  0.92, 0.75, 0.40],
      [1000,  0.98, 0.90, 0.60],
      [3000,  1.00, 1.00, 0.85],
    ],
    opacity: [
      [-1000, 0.00],
      [ 150,  0.00],
      [ 220,  0.08],
      [ 350,  0.45],
      [ 550,  0.78],
      [ 900,  0.94],
      [3000,  1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.18,
    diffuse: 0.82,
    specular: 0.15,
    specularPower: 8,
  },

  // ────────────────────────── КОЖА ──────────────────────────────

  'ct-skin': {
    name: 'Кожа',
    category: 'skin',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -200, 0.68, 0.52, 0.40],
      [ -100, 0.78, 0.60, 0.48],
      [  -30, 0.86, 0.68, 0.55],
      [   30, 0.82, 0.62, 0.50],
      [  100, 0.75, 0.58, 0.48],
      [  500, 0.70, 0.70, 0.70],
    ],
    opacity: [
      [-1000, 0.00],
      [ -500, 0.00],
      [ -200, 0.00],
      [ -150, 0.40],
      [ -100, 0.60],
      [  -50, 0.65],
      [    0, 0.45],
      [   50, 0.20],
      [  100, 0.05],
      [  200, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [  5, 0.00],
      [ 20, 0.30],
      [ 60, 0.85],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.28,
    diffuse: 0.72,
    specular: 0.10,
    specularPower: 5,
  },

  'ct-skin-transparent': {
    name: 'Кожа (прозрачная)',
    category: 'skin',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  -80, 0.75, 0.58, 0.45],
      [    0, 0.80, 0.62, 0.50],
      [  200, 0.85, 0.75, 0.62],
      [  500, 0.95, 0.92, 0.82],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [ -100, 0.04],
      [  -30, 0.06],
      [   30, 0.03],
      [  150, 0.02],
      [  200, 0.08],
      [  250, 0.25],
      [  500, 0.60],
      [ 1000, 0.82],
      [ 3000, 0.90],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.60],
      [120, 1.00],
    ],
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.15,
    specularPower: 8,
  },

  // ────────────────────────── МЫШЦЫ ─────────────────────────────

  'ct-muscle': {
    name: 'Мышцы',
    category: 'muscle',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  -80, 0.45, 0.30, 0.20],
      [    0, 0.55, 0.22, 0.18],
      [   30, 0.72, 0.28, 0.22],
      [   55, 0.82, 0.32, 0.24],
      [   80, 0.78, 0.35, 0.28],
      [  130, 0.70, 0.42, 0.35],
      [  300, 0.88, 0.82, 0.68],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -150, 0.00],
      [  -80, 0.02],
      [  -30, 0.04],
      [    5, 0.10],
      [   25, 0.45],
      [   45, 0.70],
      [   65, 0.65],
      [   90, 0.40],
      [  140, 0.15],
      [  250, 0.08],
      [  500, 0.12],
      [ 1000, 0.12],
      [ 3000, 0.10],
    ],
    gradient: [
      [  0, 0.10],
      [ 10, 0.20],
      [ 30, 0.50],
      [ 80, 0.80],
      [200, 1.00],
    ],
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.08,
    specularPower: 5,
  },

  // ────────────────────────── СОСУДЫ ────────────────────────────

  'ct-vascular': {
    name: 'Сосуды (КТА)',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  100, 0.40, 0.08, 0.06],
      [  160, 0.82, 0.16, 0.10],
      [  220, 0.95, 0.22, 0.12],
      [  320, 1.00, 0.35, 0.20],
      [  450, 1.00, 0.50, 0.30],
      [  600, 0.95, 0.80, 0.55],
      [ 1000, 1.00, 1.00, 0.90],
    ],
    opacity: [
      [-1000, 0.00],
      [  100, 0.00],
      [  130, 0.00],
      [  150, 0.20],
      [  200, 0.70],
      [  300, 0.88],
      [  420, 0.92],
      [  480, 0.35],
      [  550, 0.30],
      [  650, 0.55],
      [  800, 0.80],
      [ 1000, 0.90],
      [ 1500, 0.95],
      [ 3000, 1.00],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.50,
    specularPower: 24,
  },

  'ct-vascular-bone': {
    name: 'Сосуды + кости',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  150, 0.82, 0.15, 0.12],
      [  300, 0.95, 0.28, 0.18],
      [  500, 0.88, 0.82, 0.65],
      [  800, 0.95, 0.92, 0.82],
      [ 2000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  100, 0.00],
      [  150, 0.15],
      [  230, 0.65],
      [  380, 0.80],
      [  480, 0.25],
      [  550, 0.40],
      [  800, 0.70],
      [ 3000, 0.85],
    ],
    gradient: null,
    opacityUnitDistance: 18.0,
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.30,
    specularPower: 14,
  },

  'ct-coronary': {
    name: 'Коронарные артерии',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  120, 0.60, 0.10, 0.08],
      [  200, 0.92, 0.22, 0.14],
      [  320, 1.00, 0.38, 0.24],
      [  450, 1.00, 0.90, 0.45],
      [  800, 0.90, 0.90, 0.80],
      [ 2000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  110, 0.00],
      [  140, 0.12],
      [  200, 0.65],
      [  300, 0.85],
      [  400, 0.80],
      [  430, 0.70],
      [  480, 0.25],
      [  520, 0.15],
      [  600, 0.45],
      [  800, 0.88],
      [ 1200, 0.95],
      [ 3000, 1.00],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.12,
    diffuse: 0.88,
    specular: 0.50,
    specularPower: 24,
  },

  // ────────────────────────── ДЫХАТЕЛЬНЫЕ ПУТИ ──────────────────

  'ct-airways': {
    name: 'Дых. пути (бронхи)',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.10, 0.20, 0.45],
      [ -950, 0.20, 0.35, 0.70],
      [ -850, 0.30, 0.50, 0.88],
      [ -700, 0.45, 0.62, 0.92],
      [ -300, 0.50, 0.50, 0.55],
      [    0, 0.35, 0.30, 0.30],
      [   50, 0.55, 0.48, 0.42],
    ],
    opacity: [
      [-1000, 0.00],
      [ -995, 0.00],
      [ -970, 0.08],
      [ -900, 0.40],
      [ -800, 0.60],
      [ -700, 0.45],
      [ -550, 0.15],
      [ -300, 0.05],
      [ -100, 0.08],
      [    0, 0.25],
      [   50, 0.45],
      [  100, 0.20],
      [  200, 0.02],
      [  500, 0.00],
      [ 3000, 0.00],
    ],
    gradient: null,
    opacityUnitDistance: 25.0,
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.12,
    specularPower: 6,
  },

  'ct-lung-parenchyma': {
    name: 'Лёгкие (паренхима)',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.05, 0.05, 0.10],
      [ -900, 0.25, 0.15, 0.20],
      [ -750, 0.55, 0.30, 0.38],
      [ -600, 0.72, 0.42, 0.48],
      [ -300, 0.78, 0.58, 0.52],
      [ -100, 0.65, 0.48, 0.42],
      [    0, 0.55, 0.40, 0.35],
      [  100, 0.45, 0.35, 0.30],
    ],
    opacity: [
      [-1000, 0.00],
      [ -960, 0.00],
      [ -920, 0.04],
      [ -850, 0.25],
      [ -700, 0.50],
      [ -600, 0.65],
      [ -500, 0.75],
      [ -400, 0.80],
      [ -200, 0.82],
      [  -50, 0.68],
      [    0, 0.00],
      [ 3000, 0.00],
    ],
    gradient: null,
    opacityUnitDistance: 20.0,
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.06,
    specularPower: 4,
  },

  'ct-lung-surface': {
    name: 'Лёгкие (поверхность)',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.12, 0.12, 0.20],
      [ -800, 0.50, 0.32, 0.42],
      [ -600, 0.68, 0.45, 0.52],
      [ -400, 0.72, 0.52, 0.55],
      [ -200, 0.60, 0.48, 0.48],
    ],
    opacity: [
      [-1000, 0.00],
      [ -920, 0.00],
      [ -850, 0.10],
      [ -700, 0.30],
      [ -550, 0.50],
      [ -400, 0.35],
      [ -250, 0.10],
      [ -100, 0.01],
      [    0, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.65],
      [120, 1.00],
    ],
    opacityUnitDistance: 20.0,
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.15,
    specularPower: 8,
  },

  // ────────────────────────── ТОЛСТАЯ КИШКА ─────────────────────

  'ct-colon': {
    name: 'Толст. кишка',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.20, 0.20, 0.30],
      [ -800, 0.40, 0.38, 0.45],
      [ -200, 0.60, 0.52, 0.48],
      [    0, 0.75, 0.60, 0.50],
      [   80, 0.80, 0.55, 0.45],
      [  200, 0.70, 0.65, 0.55],
    ],
    opacity: [
      [-1000, 0.15],
      [ -950, 0.12],
      [ -600, 0.03],
      [ -200, 0.00],
      [  -50, 0.10],
      [   30, 0.40],
      [   70, 0.50],
      [  120, 0.20],
      [  200, 0.02],
      [  500, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 30, 0.60],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.10,
    specularPower: 6,
  },

  // ────────────────────────── MRI МОЗГ ──────────────────────────

  'mri-brain': {
    name: 'MRI Мозг',
    category: 'mri',
    modality: 'mri',
    color: [
      [  0,   0.00, 0.00, 0.00],
      [ 20,   0.22, 0.22, 0.28],
      [ 55,   0.45, 0.38, 0.48],
      [ 85,   0.68, 0.58, 0.58],
      [130,   0.82, 0.72, 0.68],
      [220,   0.92, 0.88, 0.82],
      [500,   1.00, 1.00, 1.00],
    ],
    opacity: [
      [  0,   0.00],
      [ 15,   0.00],
      [ 28,   0.05],
      [ 50,   0.25],
      [ 80,   0.50],
      [120,   0.65],
      [200,   0.55],
      [400,   0.40],
      [800,   0.35],
    ],
    gradient: null,
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.08,
    specularPower: 6,
  },

  'mri-wm-gm': {
    name: 'WM/GM (Б/С вещество)',
    category: 'mri',
    modality: 'mri',
    color: [
      [  0,   0.00, 0.00, 0.00],
      [ 25,   0.12, 0.12, 0.18],
      [ 50,   0.50, 0.30, 0.55],
      [ 75,   0.55, 0.38, 0.62],
      [ 95,   0.75, 0.75, 0.90],
      [130,   0.88, 0.88, 0.95],
      [250,   1.00, 1.00, 1.00],
    ],
    opacity: [
      [  0,   0.00],
      [ 20,   0.00],
      [ 38,   0.10],
      [ 55,   0.38],
      [ 80,   0.55],
      [120,   0.60],
      [200,   0.40],
      [500,   0.25],
    ],
    gradient: [
      [  0, 0.00],
      [  5, 0.00],
      [ 20, 0.50],
      [ 60, 0.90],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.10,
    specularPower: 8,
  },

  // ────────────────────────── СЕРДЦЕ ────────────────────────────

  'cardiac-3d': {
    name: 'Сердце 3D',
    category: 'cardiac',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -100, 0.80, 0.65, 0.25],
      [   30, 0.60, 0.16, 0.12],
      [   60, 0.78, 0.22, 0.16],
      [  130, 0.90, 0.30, 0.20],
      [  220, 0.96, 0.40, 0.28],
      [  380, 1.00, 0.55, 0.38],
      [  800, 1.00, 0.90, 0.60],
      [ 1500, 1.00, 1.00, 0.90],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [ -100, 0.06],
      [  -50, 0.02],
      [   15, 0.00],
      [   35, 0.30],
      [   55, 0.55],
      [   80, 0.65],
      [  150, 0.78],
      [  250, 0.88],
      [  400, 0.90],
      [  470, 0.40],
      [  550, 0.55],
      [  750, 0.72],
      [ 1500, 0.85],
      [ 3000, 0.90],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.16,
    diffuse: 0.84,
    specular: 0.40,
    specularPower: 18,
  },

  'cardiac-fat': {
    name: 'Сердце + жир',
    category: 'cardiac',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -120, 0.88, 0.78, 0.30],
      [  -60, 0.92, 0.82, 0.35],
      [   50, 0.72, 0.22, 0.18],
      [  200, 0.90, 0.30, 0.22],
      [  400, 0.95, 0.90, 0.50],
      [ 1000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -150, 0.00],
      [ -120, 0.18],
      [  -60, 0.28],
      [  -20, 0.08],
      [   40, 0.20],
      [  150, 0.55],
      [  300, 0.72],
      [  480, 0.25],
      [  700, 0.04],
      [ 3000, 0.02],
    ],
    gradient: null,
    opacityUnitDistance: 28.0,
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.20,
    specularPower: 10,
  },

  // ────────────────────────── АНГИОГРАФИЯ ───────────────────────

  'angio-3d': {
    name: 'Ангио 3D',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  120, 0.45, 0.10, 0.08],
      [  180, 0.85, 0.18, 0.12],
      [  280, 0.98, 0.28, 0.18],
      [  400, 1.00, 0.45, 0.30],
      [  550, 0.92, 0.62, 0.38],
    ],
    opacity: [
      [-1000, 0.00],
      [  100, 0.00],
      [  130, 0.00],
      [  155, 0.25],
      [  200, 0.72],
      [  300, 0.90],
      [  420, 0.92],
      [  500, 0.35],
      [  580, 0.03],
      [  700, 0.00],
      [ 3000, 0.00],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.12,
    diffuse: 0.88,
    specular: 0.55,
    specularPower: 28,
  },

  // ────────────────────────── ПЛОД ──────────────────────────────

  'fetus': {
    name: 'Плод 3D',
    category: 'obstetric',
    modality: 'ct',
    color: [
      [  0,   0.00, 0.00, 0.00],
      [ 30,   0.72, 0.58, 0.48],
      [ 70,   0.84, 0.65, 0.52],
      [120,   0.90, 0.72, 0.60],
      [200,   0.94, 0.82, 0.70],
      [400,   1.00, 1.00, 0.92],
    ],
    opacity: [
      [  0,   0.00],
      [ 15,   0.00],
      [ 30,   0.10],
      [ 55,   0.30],
      [ 90,   0.55],
      [140,   0.68],
      [200,   0.75],
      [300,   0.82],
      [400,   0.88],
      [500,   0.92],
      [800,   0.95],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 30, 0.60],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.08,
    specularPower: 4,
  },

  // ────────────────────────── СПЕЦИАЛЬНЫЕ ───────────────────────

  'ct-overview': {
    name: 'Обзорный VRT',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 0.00, 0.00, 0.08],
      [ -700, 0.25, 0.20, 0.45],
      [ -100, 0.55, 0.40, 0.35],
      [   40, 0.70, 0.35, 0.30],
      [  200, 0.85, 0.62, 0.42],
      [  500, 0.95, 0.90, 0.75],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -800, 0.02],
      [ -500, 0.04],
      [ -100, 0.03],
      [   30, 0.08],
      [  100, 0.10],
      [  250, 0.30],
      [  500, 0.60],
      [ 1000, 0.85],
      [ 3000, 1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.15,
    specularPower: 8,
  },

  'ct-xray': {
    name: 'Рентген (DRR)',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 1.00, 1.00, 1.00],
      [ 3000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.000],
      [ -600, 0.002],
      [ -200, 0.005],
      [    0, 0.008],
      [  100, 0.012],
      [  300, 0.040],
      [  700, 0.080],
      [ 1500, 0.120],
      [ 3000, 0.150],
    ],
    gradient: null,
    shade: false,
    ambient: 1.0,
    diffuse: 0.0,
    specular: 0.0,
    specularPower: 1,
  },

  'ct-density-map': {
    name: 'Карта плотности',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 0.00, 0.00, 0.08],
      [ -600, 0.00, 0.00, 0.55],
      [ -200, 0.00, 0.45, 0.78],
      [    0, 0.00, 0.78, 0.38],
      [   50, 0.55, 0.88, 0.00],
      [  200, 1.00, 0.78, 0.00],
      [  500, 1.00, 0.28, 0.00],
      [ 1000, 1.00, 0.00, 0.00],
      [ 3000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -800, 0.02],
      [ -400, 0.05],
      [    0, 0.08],
      [   60, 0.12],
      [  200, 0.30],
      [  500, 0.60],
      [ 1000, 0.85],
      [ 3000, 1.00],
    ],
    gradient: null,
    shade: false,
    ambient: 1.0,
    diffuse: 0.0,
    specular: 0.0,
    specularPower: 1,
  },

  // ══════════════════════════════════════════════════════════════════
  //  EXPERIMENTAL PRESETS (15 исходных + 3 органа брюшной полости, [11.1.15])
  // ══════════════════════════════════════════════════════════════════

  'custom-01-ct-liver': {
    name: '[01] Печень и ОБП',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [    0, 0.38, 0.18, 0.12],
      [   40, 0.65, 0.28, 0.22],
      [   70, 0.78, 0.42, 0.28],
      [  120, 0.85, 0.58, 0.42],
      [  300, 0.90, 0.85, 0.75],
      [ 1000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [   20, 0.00],
      [   30, 0.08],
      [   45, 0.40],
      [   60, 0.58],
      [   80, 0.65],
      [  150, 0.30],
      [  300, 0.08],
      [ 1000, 0.04],
    ],
    gradient: [
      [  0, 0.05],
      [ 10, 0.15],
      [ 40, 0.55],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.10,
    specularPower: 5,
  },

  'custom-02-ct-kidneys': {
    name: '[02] Почки (Урография)',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   40, 0.55, 0.18, 0.08],
      [   80, 0.78, 0.38, 0.12],
      [  150, 0.90, 0.68, 0.18],
      [  300, 1.00, 0.88, 0.48],
      [  800, 1.00, 1.00, 0.80],
    ],
    opacity: [
      [-1000, 0.00],
      [   60, 0.00],
      [   80, 0.12],
      [  120, 0.62],
      [  200, 0.85],
      [  400, 0.38],
      [ 1000, 0.08],
    ],
    gradient: null,
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.15,
    specularPower: 8,
  },

  'custom-03-ct-fat': {
    name: '[03] Жировая ткань',
    category: 'special',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -150, 0.68, 0.78, 0.28],
      [ -100, 0.78, 0.88, 0.38],
      [  -50, 0.88, 0.94, 0.48],
      [    0, 0.58, 0.38, 0.28],
      [  100, 0.48, 0.48, 0.48],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [ -140, 0.22],
      [ -100, 0.55],
      [  -60, 0.45],
      [  -30, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 50, 0.80],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.05,
    specularPower: 2,
  },

  'custom-04-ct-brain-vessels': {
    name: '[04] Сосуды мозга',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   60, 0.45, 0.08, 0.08],
      [  120, 0.78, 0.12, 0.18],
      [  200, 0.95, 0.18, 0.28],
      [  300, 1.00, 0.38, 0.48],
      [  600, 0.75, 0.75, 0.75],
    ],
    opacity: [
      [-1000, 0.00],
      [   80, 0.00],
      [  110, 0.12],
      [  160, 0.65],
      [  260, 0.85],
      [  360, 0.90],
      [  420, 0.08],
      [  600, 0.00],
      [ 1000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 60, 0.50],
      [180, 1.00],
    ],
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.42,
    specularPower: 20,
  },

  'custom-05-ct-carotid': {
    name: '[05] Сонные артерии',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   80, 0.55, 0.08, 0.08],
      [  150, 0.88, 0.18, 0.12],
      [  300, 1.00, 0.48, 0.28],
      [  600, 0.88, 0.78, 0.68],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [   90, 0.00],
      [  130, 0.18],
      [  190, 0.65],
      [  320, 0.82],
      [  450, 0.25],
      [  600, 0.45],
      [ 1000, 0.65],
    ],
    gradient: null,
    shade: true,
    ambient: 0.18,
    diffuse: 0.82,
    specular: 0.30,
    specularPower: 12,
  },

  'custom-06-ct-dental': {
    name: '[06] Имплантология',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  500, 0.78, 0.72, 0.62],
      [ 1000, 0.90, 0.88, 0.80],
      [ 1500, 0.95, 0.95, 0.90],
      [ 2500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  400, 0.00],
      [  600, 0.12],
      [ 1000, 0.62],
      [ 1500, 0.92],
      [ 3000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 50, 0.00],
      [150, 0.70],
      [300, 1.00],
    ],
    shade: true,
    ambient: 0.10,
    diffuse: 0.90,
    specular: 0.60,
    specularPower: 40,
  },

  'custom-07-ct-fracture': {
    name: '[07] Переломы',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  200, 0.58, 0.58, 0.62],
      [  400, 0.78, 0.78, 0.82],
      [  800, 0.94, 0.94, 0.98],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  150, 0.00],
      [  250, 0.22],
      [  450, 0.78],
      [  800, 0.92],
      [ 2000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 25, 0.00],
      [ 80, 0.75],
      [250, 1.00],
    ],
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.20,
    specularPower: 15,
  },

  'custom-08-ct-mip-pseudo': {
    name: '[08] Псевдо-MIP',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 1.00, 1.00, 1.00],
      [ 3000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [    0, 0.005],
      [  200, 0.020],
      [  500, 0.050],
      [ 1000, 0.080],
      [ 3000, 0.120],
    ],
    gradient: null,
    shade: false,
    ambient: 1.00,
    diffuse: 0.00,
    specular: 0.00,
    specularPower: 1,
  },

  'custom-09-mri-angio': {
    name: '[09] МР-ангио (TOF)',
    category: 'mri',
    modality: 'mri',
    color: [
      [   0, 0.00, 0.00, 0.00],
      [  50, 0.48, 0.08, 0.08],
      [ 150, 0.85, 0.18, 0.12],
      [ 300, 1.00, 0.38, 0.18],
      [ 600, 1.00, 0.78, 0.48],
      [1000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [   0, 0.00],
      [  80, 0.00],
      [ 120, 0.18],
      [ 200, 0.62],
      [ 400, 0.88],
      [1000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.50],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.40,
    specularPower: 20,
  },

  'custom-10-mri-t2-fluid': {
    name: '[10] МРТ T2 (Жидкости)',
    category: 'mri',
    modality: 'mri',
    color: [
      [   0, 0.00, 0.00, 0.00],
      [ 100, 0.08, 0.18, 0.48],
      [ 200, 0.18, 0.38, 0.78],
      [ 400, 0.38, 0.68, 1.00],
      [ 800, 0.78, 0.88, 1.00],
      [1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [   0, 0.00],
      [ 150, 0.00],
      [ 250, 0.22],
      [ 400, 0.62],
      [ 800, 0.90],
      [1500, 1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.10,
    specularPower: 5,
  },

  'custom-11-ct-cardiac-valves': {
    name: '[11] Клапаны (Кальций)',
    category: 'cardiac',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  150, 0.55, 0.08, 0.08],
      [  300, 0.78, 0.28, 0.18],
      [  500, 1.00, 0.78, 0.28],
      [  800, 1.00, 1.00, 0.58],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  130, 0.00],
      [  180, 0.05],
      [  300, 0.22],
      [  450, 0.72],
      [  700, 0.92],
      [ 1500, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 70, 0.70],
      [200, 1.00],
    ],
    shade: true,
    ambient: 0.10,
    diffuse: 0.90,
    specular: 0.50,
    specularPower: 25,
  },

  'custom-12-ct-pulmonary-vessels': {
    name: '[12] Легочные сосуды',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -200, 0.18, 0.18, 0.38],
      [   50, 0.68, 0.08, 0.18],
      [  150, 0.90, 0.28, 0.18],
      [  300, 1.00, 0.48, 0.28],
      [  600, 0.88, 0.88, 0.88],
    ],
    opacity: [
      [-1000, 0.00],
      [ -300, 0.00],
      [ -100, 0.01],
      [   50, 0.15],
      [  130, 0.72],
      [  260, 0.88],
      [  420, 0.08],
      [  700, 0.02],
      [ 1000, 0.01],
    ],
    gradient: [
      [  0, 0.00],
      [ 15, 0.00],
      [ 50, 0.55],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.32,
    specularPower: 14,
  },

  'custom-13-ct-bronchi-lumen': {
    name: '[13] Эндоскопия (Бронхи)',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.82, 0.52, 0.42],
      [ -800, 0.85, 0.55, 0.45],
      [ -400, 0.90, 0.60, 0.50],
      [    0, 0.95, 0.70, 0.60],
      [ 1000, 1.00, 0.80, 0.70],
    ],
    opacity: [
      [-1000, 0.00],
      [ -950, 0.00],
      [ -800, 0.32],
      [ -600, 0.62],
      [ -400, 0.82],
      [    0, 0.92],
      [ 1000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.60],
      [120, 1.00],
    ],
    shade: true,
    ambient: 0.28,
    diffuse: 0.72,
    specular: 0.10,
    specularPower: 5,
  },

  'custom-14-ct-aorta-stent': {
    name: '[14] Стенты / Металл',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  150, 0.68, 0.18, 0.08],
      [  300, 0.88, 0.28, 0.18],
      [  600, 0.95, 0.88, 0.78],
      [ 1200, 1.00, 1.00, 0.58],
      [ 2500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  120, 0.00],
      [  200, 0.12],
      [  350, 0.32],
      [  600, 0.82],
      [ 1200, 0.95],
      [ 3000, 1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.55,
    specularPower: 32,
  },

  'custom-15-ct-muscle-skeletal': {
    name: '[15] Костно-мышечная',
    category: 'muscle',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   20, 0.58, 0.18, 0.12],
      [   60, 0.78, 0.28, 0.18],
      [  150, 0.85, 0.48, 0.38],
      [  300, 0.90, 0.85, 0.75],
      [  800, 0.95, 0.95, 0.90],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [    0, 0.00],
      [   30, 0.18],
      [   80, 0.35],
      [  150, 0.12],
      [  250, 0.42],
      [  500, 0.82],
      [ 1000, 0.95],
      [ 3000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 15, 0.00],
      [ 50, 0.50],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.15,
    specularPower: 8,
  },

  // ══════════════════════════════════════════════════════════════════
  //  [11.1.15] ОРГАНЫ БРЮШНОЙ ПОЛОСТИ — по просьбе владельца.
  //
  //  ЧТО ЗДЕСЬ ВОЗМОЖНО, А ЧТО НЕТ. Кривая переноса различает только
  //  ПЛОТНОСТЬ. На нативном КТ печень 55–65 HU, селезёнка 40–50,
  //  поджелудочная 30–50, корковое вещество почки 30–45 — полосы
  //  перекрываются, и никакой пресет не отделит один орган от соседа.
  //  Разделяет их КОНТРАСТ: в портальную фазу селезёнка 110–140,
  //  поджелудочная 80–110 (пик в панкреатическую, 40–50 с), почка в
  //  нефрографическую 120–200. На эти окна пресеты и настроены.
  //  На нативе они показывают орган слабее и вместе с соседями — это
  //  предел метода, а не настройки.
  //
  //  Кость (>300 HU) в двух нижних пресетах ПОДАВЛЕНА: иначе рёбра и
  //  позвоночник забивают собой всю брюшную полость. У почек подавить
  //  нельзя — выведенный контраст 300–1000 HU лежит в той же полосе,
  //  что и кость, поэтому на урографии позвоночник виден всегда;
  //  отсекается он не кривой, а рамкой обрезки.
  // ══════════════════════════════════════════════════════════════════

  'custom-16-ct-pancreas': {
    name: '[16] Поджелудочная железа',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   25, 0.46, 0.38, 0.26],
      [   60, 0.78, 0.66, 0.44],
      [  100, 0.93, 0.84, 0.60],   // пик панкреатической фазы — светлая охра
      [  150, 0.96, 0.91, 0.78],
      [  300, 0.86, 0.60, 0.54],   // сосуды рядом — приглушённо-красным
      [ 3000, 0.70, 0.70, 0.70],
    ],
    opacity: [
      [-1000, 0.00],
      [   20, 0.00],               // жир и жидкость отсечены
      [   30, 0.08],               // нативная железа едва проступает
      [   45, 0.35],
      [   70, 0.62],
      [  100, 0.80],               // панкреатическая фаза — максимум
      [  130, 0.55],               // дальше начинают спорить печень и селезёнка
      [  180, 0.20],
      [  300, 0.05],
      [ 3000, 0.02],               // кость подавлена
    ],
    gradient: [
      [  0, 0.02],
      [  8, 0.20],
      [ 30, 0.70],
      [ 80, 1.00],                 // границы органа — резче, чем однородная паренхима
    ],
    shade: true,
    ambient: 0.24,
    diffuse: 0.76,
    specular: 0.12,
    specularPower: 6,
  },

  'custom-17-ct-spleen': {
    name: '[17] Селезёнка',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   30, 0.40, 0.17, 0.26],
      [   60, 0.62, 0.26, 0.34],
      [  110, 0.79, 0.37, 0.43],   // портальная фаза — характерный багровый
      [  150, 0.88, 0.53, 0.53],
      [  300, 0.95, 0.86, 0.76],
      [ 3000, 0.70, 0.70, 0.70],
    ],
    opacity: [
      [-1000, 0.00],
      [   25, 0.00],
      [   35, 0.10],               // нативная селезёнка 40–50 HU
      [   45, 0.42],
      [   60, 0.50],
      [  110, 0.82],               // портальная фаза — максимум
      [  150, 0.72],
      [  220, 0.25],
      [  400, 0.06],
      [ 3000, 0.03],               // кость подавлена
    ],
    gradient: [
      [  0, 0.03],
      [ 10, 0.22],
      [ 35, 0.72],
      [ 90, 1.00],
    ],
    shade: true,
    ambient: 0.23,
    diffuse: 0.77,
    specular: 0.14,
    specularPower: 7,
  },

  'custom-18-ct-kidney-native': {
    name: '[18] Почки (паренхима)',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   25, 0.48, 0.22, 0.16],
      [   45, 0.68, 0.32, 0.22],   // нативная паренхима 30–45 HU
      [  120, 0.86, 0.52, 0.30],   // нефрографическая фаза
      [  200, 0.94, 0.72, 0.38],
      [  350, 0.80, 0.60, 0.45],
      [ 3000, 0.60, 0.60, 0.60],
    ],
    opacity: [
      [-1000, 0.00],
      [   15, 0.00],               // паранефральный жир и киста отсечены
      [   25, 0.12],
      [   38, 0.52],               // ← натив: корковое вещество ВИДНО
      [   70, 0.62],
      [  130, 0.82],               // контраст: нефрографическая фаза
      [  200, 0.78],
      [  300, 0.30],               // выведенный контраст приглушён: он для урографии
      [  600, 0.10],
      [ 3000, 0.05],               // кость подавлена
    ],
    gradient: [
      [  0, 0.03],
      [ 10, 0.25],
      [ 40, 0.75],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.24,
    diffuse: 0.76,
    specular: 0.12,
    specularPower: 6,
  },
};

// ══════════════════════════════════════════════════════════════════
// 4. VALIDATION ON INIT
// ══════════════════════════════════════════════════════════════════

try {
  for (const [key, preset] of Object.entries(_PRESETS_CONFIG)) {
    validatePreset(preset, key);
  }
  log.info(`[Presets3D] ✅ All ${Object.keys(_PRESETS_CONFIG).length} presets validated`);
} catch (e) {
  log.error('[Presets3D] ❌ Preset validation failed:', (e as Error).message);
  throw e;
}

// ══════════════════════════════════════════════════════════════════
// 5. MAPS & FROZEN STATE
// ══════════════════════════════════════════════════════════════════

const _customPresets = new Map<string, Preset3D>();

export const PRESETS_3D = deepFreeze(_PRESETS_CONFIG);

export const PRESET_ALIAS = deepFreeze({
  'ct-bone-3d':   'ct-bone-clean',
  'ct-skin':      'ct-skin',
  'ct-muscle':    'ct-muscle',
  'ct-vascular':  'ct-vascular',
  'ct-air':       'ct-airways',
  'ct-lung-3d':   'ct-lung-parenchyma',
  'ct-colon':     'ct-colon',
  'mri-brain-3d': 'mri-brain',
  'mri-wm-gm':    'mri-wm-gm',
  'cardiac-3d':   'cardiac-3d',
  'angio-3d':     'angio-3d',
  'fetus':        'fetus',
} as const);

// ══════════════════════════════════════════════════════════════════
// 6. ENCAPSULATED REGISTRY (Singleton Pattern)
// ══════════════════════════════════════════════════════════════════

class PresetRegistry {
  #presets: Readonly<Record<string, Preset3D>>;
  #aliases: Readonly<Record<string, string>>;
  #allPresetsCache: ReadonlyArray<Readonly<{ key: string; name: string; category: string; modality?: string }>>;
  #categoryCache = new Map<string, ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>>>();
  #modalityCache = new Map<string, ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>>>();

  constructor(presets: Record<string, Preset3D>, aliases: Record<string, string>) {
    this.#presets = presets;
    this.#aliases = aliases;
    
    // Pre-compute cache для оптимизации производительности
    this.#allPresetsCache = Object.freeze(
      Object.entries(presets).map(([key, p]) => 
        Object.freeze({ key, name: p.name, category: p.category, modality: p.modality })
      )
    );
  }

  resolve(key: string): Preset3D | undefined {
    const resolvedKey = this.#aliases[key] ?? key;
    return this.#presets[resolvedKey] ?? _customPresets.get(resolvedKey);
  }

  listAll(): ReadonlyArray<Readonly<{ key: string; name: string; category: string; modality?: string }>> {
    return this.#allPresetsCache;
  }

  getByCategory(category: string): ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>> {
    const cached = this.#categoryCache.get(category);
    if (cached) return cached;

    const result = Object.freeze(
      Object.entries(this.#presets)
        .filter(([_, p]) => p.category === category)
        .map(([key, p]) => Object.freeze({ key, name: p.name, preset: p }))
    );
      
    this.#categoryCache.set(category, result);
    return result;
  }

  getByModality(modality: string): ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>> {
    const cached = this.#modalityCache.get(modality);
    if (cached) return cached;

    const result = Object.freeze(
      Object.entries(this.#presets)
        .filter(([_, p]) => p.modality === modality || p.modality === 'any')
        .map(([key, p]) => Object.freeze({ key, name: p.name, preset: p }))
    );
      
    this.#modalityCache.set(modality, result);
    return result;
  }

  addCustomPreset(key: string, preset: Preset3D): Result<void, Error> {
    try {
      validatePreset(preset, key);
      _customPresets.set(key, preset);
      log.info(`[Presets3D] ✅ Custom preset added: ${preset.name} (${key})`);
      return Ok(undefined);
    } catch (err) {
      return Err(err instanceof Error ? err : new Error(String(err)));
    }
  }

  removeCustomPreset(key: string): boolean {
    const removed = _customPresets.delete(key);
    if (removed) {
      log.info(`[Presets3D] ✅ Custom preset removed: ${key}`);
    }
    return removed;
  }

  invalidateCache(): void {
    this.#categoryCache.clear();
    this.#modalityCache.clear();
  }
}

export const registry = new PresetRegistry(PRESETS_3D, PRESET_ALIAS);

// ══════════════════════════════════════════════════════════════════
// 7. VTK ADAPTER ENGINE (Safe & Strict)
// ══════════════════════════════════════════════════════════════════

class VtkPresetEngine {
  static apply(actor: IVtkVolumeActor, preset: Preset3D): Result<void, Error> {
    const property = actor.getProperty?.() ?? actor.property;
    if (!property) {
      return Err(new Error('VTK VolumeProperty is undefined or missing.'));
    }

    try {
      this.#applyColor(property, preset.color);
      this.#applyOpacity(property, preset.opacity);
      this.#applyGradient(property, preset.gradient);
      this.#applyShading(property, preset);
      return Ok(undefined);
    } catch (err) {
      return Err(err instanceof Error ? err : new Error(String(err)));
    }
  }

  static #resolveTransferFunction(
    getter: (() => IVtkTransferFunction | IVtkTransferFunction[] | undefined) | undefined
  ): IVtkTransferFunction | null {
    if (!getter) return null;
    const fn = getter();
    if (Array.isArray(fn)) return fn[0] ?? null;
    return fn && typeof fn.removeAllPoints === 'function' ? fn : null;
  }

  static #applyColor(property: IVtkVolumeProperty, points: readonly ColorPoint[]): void {
    const ctfun = this.#resolveTransferFunction(property.getRGBTransferFunction?.bind(property));
    if (!ctfun) return;

    ctfun.removeAllPoints();
    for (const [hu, r, g, b] of points) {
      ctfun.addRGBPoint(hu, r, g, b);
    }
  }

  static #applyOpacity(property: IVtkVolumeProperty, points: readonly OpacityPoint[]): void {
    const ofun = property.getScalarOpacity?.(0);
    if (!ofun || typeof ofun.removeAllPoints !== 'function') return;

    ofun.removeAllPoints();
    for (const [hu, alpha] of points) {
      // [11.1.23] Глобальный масштаб непрозрачности («Плотность тканей»).
      // Владелец заметил: при вращении органы видны ЛУЧШЕ, чем в покое.
      // Физика такова: vtk во время взаимодействия увеличивает шаг луча
      // (autoAdjustSampleDistances), выборок на луч меньше — накопленная
      // непрозрачность падает — оболочка мышц/кожи истончается и интерьер
      // проступает. Красивый «вид при вращении» — это недосэмплированный
      // рендер. Масштаб ниже даёт ТОТ ЖЕ эффект честно и управляемо, без
      // потери качества выборки, на любом пресете.
      ofun.addPoint(hu, Math.min(1, alpha * _множитель(hu)));   // [11.1.47] гасим избирательно
    }
    // [11.1.25→11.1.36] Кривая регистрируется В РЕЕСТРЕ (по своей функции):
    // «Плотность тканей» правит все живые окна сразу, а не одно последнее.
    const свой = _кривые.findIndex((к) => к.ofun === ofun);
    if (свой >= 0) _кривые[свой] = { ofun, points };
    else { _кривые.push({ ofun, points }); if (_кривые.length > 12) _кривые.shift(); }
  }

  static #applyGradient(property: IVtkVolumeProperty, points?: readonly GradientPoint[] | null): void {
    if (!property.getGradientOpacity || !property.setUseGradientOpacity) return;

    try {
      const gofun = property.getGradientOpacity(0);
      if (points && gofun && typeof gofun.removeAllPoints === 'function') {
        gofun.removeAllPoints();
        for (const [mag, alpha] of points) {
          gofun.addPoint(mag, alpha);
        }
        property.setUseGradientOpacity(0, true);
      } else {
        property.setUseGradientOpacity(0, false);
      }
    } catch {
      property.setUseGradientOpacity(0, false);
    }
  }

  static #applyShading(property: IVtkVolumeProperty, preset: Preset3D): void {
    const setSafe = <K extends keyof IVtkVolumeProperty>(method: K, value: Parameters<IVtkVolumeProperty[K]>[0]) => {
      const fn = property[method];
      if (typeof fn === 'function') {
        (fn as Function).call(property, value);
      }
    };

    setSafe('setShade', preset.shade ?? true);
    setSafe('setAmbient', preset.ambient ?? 0.2);
    setSafe('setDiffuse', preset.diffuse ?? 0.8);
    setSafe('setSpecular', preset.specular ?? 0.2);
    setSafe('setSpecularPower', preset.specularPower ?? 10);
    
    if (typeof property.setScalarOpacityUnitDistance === 'function') {
      property.setScalarOpacityUnitDistance(0, preset.opacityUnitDistance ?? 4.0);
    }
  }
}

// ══════════════════════════════════════════════════════════════════
// 7b. ГЛОБАЛЬНЫЙ МАСШТАБ НЕПРОЗРАЧНОСТИ [11.1.23]
// ══════════════════════════════════════════════════════════════════

let _масштабНепрозрачности = 1;
/**
 * [11.1.47] ИЗБИРАТЕЛЬНОЕ ГАШЕНИЕ. «Плотность тканей» умножает ВСЮ кривую —
 * бледнеет и жир, и паренхима; владелец справедливо заметил, что органы
 * тускнеют вместе со стенкой, а я об этом умалчивал. Здесь — гашение
 * ТОЛЬКО ниже порога плотности: мягкие ткани уходят в прозрачность, а
 * паренхима и контрастированные сосуды остаются во весь голос и на их фоне
 * выглядят темнее и резче.
 *
 * порогHU — граница «мягкое / орган» (по умолчанию 70 HU: жир −100…−50,
 * мышца 40…60 остаются ниже; печень 60…80, селезёнка с контрастом 100…140,
 * сосуды 200+ — выше). переходHU — ширина плавного перехода, чтобы граница
 * не резала ткань ступенькой.
 */
let _мягкость = { коэф: 1, порогHU: 70, переходHU: 25 };

/** Множитель непрозрачности для конкретной плотности. */
function _множитель(hu: number): number {
  const { коэф, порогHU, переходHU } = _мягкость;
  if (коэф >= 1) return _масштабНепрозрачности;
  const низ = порогHU - переходHU;
  let доля: number;
  if (hu <= низ) доля = 1;                                   // целиком мягкая ткань
  else if (hu >= порогHU) доля = 0;                          // целиком орган
  else доля = (порогHU - hu) / (порогHU - низ);              // плавный переход
  return _масштабНепрозрачности * (коэф * доля + 1 * (1 - доля));
}
/**
 * [11.1.36] РЕЕСТР кривых вместо одной «последней». Одна переменная и была
 * ямой: у КАЖДОГО объёмного окна свой актёр и своя функция непрозрачности, а
 * помнили мы только последнюю применённую. Врач смотрит «3D пресет»
 * (шестое окно MPR6), двигает «Плотность тканей» — а ползунок честно мутирует
 * кривую ДРУГОГО окна и возвращает true: «движется, но ничего не делает»
 * (замечание владельца, телефон). Теперь помним кривые всех живых окон и
 * масштабируем каждую; мёртвые (без removeAllPoints) выбрасываются на ходу.
 */
let _кривые: Array<{ ofun: any; points: readonly OpacityPoint[] }> = [];

/**
 * Задать масштаб 0.05…1.
 * @returns {boolean} применилось ли ПРЯМО СЕЙЧАС к живому объёму (вызывающему
 * остаётся только перерисовать окна). false — объём ещё не построен, масштаб
 * ляжет с ближайшим пресетом.
 */
/**
 * [11.1.47] Прозрачность МЯГКИХ ТКАНЕЙ отдельно от органов.
 * @param коэф 0.05…1 — во сколько раз гасить всё, что ниже порога (1 = не гасить)
 * @param порогHU граница «мягкое/орган», 0…400
 */
export function setSoftTissueScale(коэф: number, порогHU?: number): boolean {
  const k = Number(коэф);
  _мягкость.коэф = Number.isFinite(k) ? Math.min(1, Math.max(0.05, k)) : 1;
  const п = Number(порогHU);
  if (Number.isFinite(п)) _мягкость.порогHU = Math.min(400, Math.max(0, п));
  return setGlobalOpacityScale(_масштабНепрозрачности);   // пересчитать все живые кривые
}

export function setGlobalOpacityScale(х: number): boolean {
  const v = Number(х);
  _масштабНепрозрачности = Number.isFinite(v) ? Math.min(1, Math.max(0.05, v)) : 1;
  let легло = 0;
  _кривые = _кривые.filter((к) => {
    if (!к?.ofun?.removeAllPoints) return false;   // мёртвая — выбросить
    try {
      к.ofun.removeAllPoints();
      for (const [hu, alpha] of к.points) {
        к.ofun.addPoint(hu, Math.min(1, alpha * _множитель(hu)));
      }
      легло++;
      return true;
    } catch {
      return false;   // функция умерла между кадрами — выбросить
    }
  });
  return легло > 0;
}

export function getGlobalOpacityScale(): number {
  return _масштабНепрозрачности;
}

// ══════════════════════════════════════════════════════════════════
// 8. PUBLIC API (Result Pattern)
// ══════════════════════════════════════════════════════════════════

export function applyCustomPreset(viewport: IViewport, presetKey: string): Result<boolean, Error> {
  const preset = registry.resolve(presetKey);

  if (!preset) {
    return Err(new Error(`Unknown preset requested: "${presetKey}"`));
  }

  try {
    const actorEntry = viewport.getDefaultActor?.();
    let actor = actorEntry?.actor;
    
    if (!actor) {
      const actors = viewport.getActors?.();
      if (actors && actors.length > 0) {
        const first = actors[0];
        actor = 'actor' in first ? first.actor : first;
      }
    }
    
    if (!actor) {
      return Err(new Error('Volume actor not found in viewport.'));
    }

    const result = VtkPresetEngine.apply(actor, preset);
    if (!result.success) return result;

    viewport.render();
    log.debug(`[Presets3D] ✅ Preset applied: ${preset.name} (${presetKey})`);
    return Ok(true);

  } catch (err) {
    return Err(err instanceof Error ? err : new Error(String(err)));
  }
}

/**
 * [10.0.3] Достать сам пресет, а не только его имя.
 *
 * Нужно, чтобы перенести точки пресета на шкалу конкретной серии: у КТ это
 * HU и точки осмысленны как есть, а у МР шкала своя, и «кожа» с окном
 * −200…+200 HU на МР-данных попадает мимо всего — окно чернеет.
 */
export const getPreset3D = (key: string): Preset3D | undefined => registry.resolve(key);

export const listAllPresets = (): ReturnType<typeof registry.listAll> => registry.listAll();
export const getPresetsByCategory = (cat: string): ReturnType<typeof registry.getByCategory> => registry.getByCategory(cat);
export const getPresetsByModality = (modality: string): ReturnType<typeof registry.getByModality> => registry.getByModality(modality);
export const addCustomPreset = (key: string, preset: Preset3D): ReturnType<typeof registry.addCustomPreset> => registry.addCustomPreset(key, preset);
export const removeCustomPreset = (key: string): boolean => registry.removeCustomPreset(key);
export const invalidatePresetCache = (): void => registry.invalidateCache();